import React from 'react'
import Link from 'next/link'
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa'
import { formatMoney } from '../../utils/format-money'
import { useMarketBooking } from './MarketBooking'

// Presentational building blocks for the UAE / Malaysia pages. Visually these
// are deliberately not the Singapore hub components (dark hero overlay booking
// card, deal carousels): the market pages share the brand palette but not the
// SG layout, which also keeps them from reading as copies of the SG pages.

// tone: 'white' | 'muted' | 'dark' (slate band, as on the Singapore personal
// trainer pages). Children that print text take `dark` to match.
export const MarketSection = ({ id, title, intro, children, tone = 'white', narrow = false, headingLevel = 2 }) => {
    const Heading = `h${headingLevel}`
    const dark = tone === 'dark'
    const bg = dark ? 'bg-slate-900' : tone === 'muted' ? 'bg-slate-50' : 'bg-white'
    return (
        <section id={id} className={`${bg} py-10 md:py-14`}>
            <div className={`${narrow ? 'max-w-3xl' : 'max-w-5xl'} mx-auto px-4`}>
                {title && (
                    <Heading className={`text-2xl md:text-3xl font-extrabold tracking-tight ${dark ? 'text-white' : 'text-slate-900'}`}>
                        {title}
                    </Heading>
                )}
                {intro && <p className={`mt-3 leading-relaxed ${dark ? 'text-white/60' : 'text-slate-600'}`}>{intro}</p>}
                <div className={title || intro ? 'mt-6' : ''}>{children}</div>
            </div>
        </section>
    )
}

// Plain paragraphs from a string array (content modules store prose this way).
export const Prose = ({ paragraphs = [], dark = false }) => (
    <div className={`space-y-4 leading-relaxed ${dark ? 'text-white/70' : 'text-slate-700'}`}>
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
)

export const Checklist = ({ items = [], columns = 1, dark = false }) => (
    <ul className={`grid gap-3 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
        {items.map((item, i) => (
            <li key={i} className={`flex items-start gap-3 ${dark ? 'text-white/80' : 'text-slate-700'}`}>
                <FaCheckCircle className={`mt-1 shrink-0 ${dark ? 'text-orange-400' : 'text-emerald-500'}`} aria-hidden="true" />
                <span>{item}</span>
            </li>
        ))}
    </ul>
)

// [{ title, body }]
export const InfoCards = ({ items = [], columns = 3, dark = false }) => (
    <div className={`grid gap-4 ${columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {items.map((item, i) => (
            <div key={i} className={dark ? 'rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-md' : 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm'}>
                <h3 className={`font-bold ${dark ? 'text-white/90' : 'text-slate-900'}`}>{item.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-white/60' : 'text-slate-600'}`}>{item.body}</p>
            </div>
        ))}
    </div>
)

// Numbered steps: [{ title, body }]
export const Steps = ({ items = [], dark = false }) => (
    <ol className="space-y-4">
        {items.map((item, i) => (
            <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                    {i + 1}
                </span>
                <div>
                    <h3 className={`font-semibold ${dark ? 'text-white/90' : 'text-slate-900'}`}>{item.title}</h3>
                    <p className={`mt-1 text-sm leading-relaxed ${dark ? 'text-white/60' : 'text-slate-600'}`}>{item.body}</p>
                </div>
            </li>
        ))}
    </ol>
)

// Internal links: [{ href, title, body? }]
export const LinkCards = ({ links = [], columns = 3, dark = false }) => (
    <ul className={`grid gap-3 ${columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 md:grid-cols-3'}`}>
        {links.map((link) => (
            <li key={link.href}>
                <Link
                    href={link.href}
                    prefetch={false}
                    className={dark
                        ? 'group flex h-full items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-4 transition hover:border-orange-500/40 hover:bg-white/10'
                        : 'group flex h-full items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-orange-300 hover:shadow-md'}
                >
                    <span>
                        <span className={`block font-semibold ${dark ? 'text-white/90 group-hover:text-orange-400' : 'text-slate-900 group-hover:text-orange-600'}`}>{link.title}</span>
                        {link.body && <span className={`mt-1 block text-sm ${dark ? 'text-white/50' : 'text-slate-500'}`}>{link.body}</span>}
                    </span>
                    <FaArrowRight className={`mt-1 shrink-0 ${dark ? 'text-white/30 group-hover:text-orange-400' : 'text-slate-300 group-hover:text-orange-500'}`} aria-hidden="true" />
                </Link>
            </li>
        ))}
    </ul>
)

// Simple price table: columns [{ key, label }], rows [{ ...values }]; money
// columns are formatted in the market's currency.
export const PriceTable = ({ countryCd, columns, rows, caption, moneyKeys = [] }) => (
    <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full min-w-[28rem] text-left text-sm">
            {caption && <caption className="bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700">{caption}</caption>}
            <thead className="bg-slate-50 text-slate-500">
                <tr>
                    {columns.map((c) => <th key={c.key} scope="col" className="px-4 py-3 font-medium">{c.label}</th>)}
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row, i) => (
                    <tr key={i}>
                        {columns.map((c) => (
                            <td key={c.key} className={`px-4 py-3 ${moneyKeys.includes(c.key) ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                                {moneyKeys.includes(c.key) ? formatMoney(row[c.key], countryCd) : row[c.key]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

// Bookable price tiles. tile: { key, label, price, summary, criteria }
export const PriceTiles = ({ countryCd, tiles, note }) => {
    const { book } = useMarketBooking()
    return (
        <div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                {tiles.map((tile) => (
                    <button
                        key={tile.key}
                        type="button"
                        onClick={() => book(tile)}
                        aria-label={`Book ${tile.summary} for ${formatMoney(tile.price, countryCd)}`}
                        className="rounded-2xl border border-orange-200 bg-orange-50 px-3 py-4 text-center transition hover:-translate-y-0.5 hover:border-orange-400 hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                        <span className="block text-sm text-slate-600">{tile.label}</span>
                        <span className="mt-1 block text-lg font-extrabold text-slate-900">{formatMoney(tile.price, countryCd)}</span>
                        <span className="mt-1 block text-xs font-semibold text-orange-600">Book</span>
                    </button>
                ))}
            </div>
            {note && <p className="mt-3 text-xs text-slate-500">{note}</p>}
        </div>
    )
}

// Segmented control: options [{ value, label }]
export const Toggle = ({ label, options, value, onChange }) => (
    <div role="group" aria-label={label} className="inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1">
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                aria-pressed={value === o.value}
                onClick={() => onChange(o.value)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${value === o.value ? 'bg-white text-orange-600 shadow' : 'text-slate-600 hover:text-slate-900'}`}
            >
                {o.label}
            </button>
        ))}
    </div>
)

export const BookButton = ({ children, tile, variant = 'primary' }) => {
    const { book } = useMarketBooking()
    return (
        <button
            type="button"
            onClick={() => book(tile)}
            className={variant === 'primary'
                ? 'inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white shadow-lg transition hover:bg-orange-600'
                : 'inline-flex items-center gap-2 rounded-full border-2 border-orange-500 px-6 py-3 font-bold text-orange-600 transition hover:bg-orange-50'}
        >
            {children}
            <FaArrowRight aria-hidden="true" />
        </button>
    )
}

// Shown while the market takes requests without online payment
// (utils/markets.js paymentsEnabled).
export const BookingModeNote = ({ market }) => {
    if (market.paymentsEnabled) return null
    return (
        <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <FaCheckCircle className="text-emerald-500" aria-hidden="true" />
            Book online and confirm in the app - no card needed now.
        </p>
    )
}

// Lead paragraph and fact chips under the SG-style overlay heroes. The hero card
// only has room for a one-line subtitle, so the page's lead (its most specific
// copy) sits here, right below the hero and breadcrumb.
export const IntroStrip = ({ eyebrow, lead, facts = [] }) => (
    <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 pb-8">
            {eyebrow && <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">{eyebrow}</p>}
            {lead && <p className="mt-2 text-lg leading-relaxed text-slate-700">{lead}</p>}
            {facts.length > 0 && (
                <ul className="mt-4 flex flex-wrap gap-2">
                    {facts.map((f) => (
                        <li key={f} className="rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">{f}</li>
                    ))}
                </ul>
            )}
        </div>
    </section>
)
